import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText, Download, Filter, Calendar, Search, RefreshCw,
    CheckCircle, Clock, AlertCircle, Mail, TrendingUp, Users,
    BarChart3, PieChart, Activity, ChevronLeft, ChevronRight,
    Bot, FileSpreadsheet, X, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { baseURL } from '../config/APIConfig';

const AIAgentReports = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [processedEmails, setProcessedEmails] = useState([]);
    const [stats, setStats] = useState({
        totalProcessed: 0,
        successRate: 0,
        avgResponseTime: 0,
        totalQuotes: 0,
        totalBookings: 0,
        totalHBLs: 0
    });

    // Filters
    const [selectedAgent, setSelectedAgent] = useState('all');
    const [dateRange, setDateRange] = useState('7d');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(15);

    // Custom date range
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Fetch all data on mount
    useEffect(() => {
        fetchAllData();
    }, []);

    // Refetch when filters change
    useEffect(() => {
        if (agents.length > 0) {
            fetchFilteredData();
        }
    }, [selectedAgent, dateRange, statusFilter]);

    const fetchAllData = async () => {
        setIsLoading(true);
        try {
            // Fetch agents
            const agentsRes = await fetch(`${API_URL}/api/ai-agents`);
            const agentsData = await agentsRes.json();
            if (agentsData.success) {
                setAgents(agentsData.agents || []);
            }

            // Fetch all activity logs
            const logsRes = await fetch(`${API_URL}/api/ai-agents/activity-logs?limit=500`);
            const logsData = await logsRes.json();
            if (logsData.success) {
                setActivityLogs(logsData.activityLogs || []);
            }

            // Fetch processed emails for all agents
            await fetchFilteredData();

        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load report data');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFilteredData = async () => {
        try {
            let allProcessedEmails = [];
            const agentsToFetch = selectedAgent === 'all' ? agents : agents.filter(a => a.id === selectedAgent);

            for (const agent of agentsToFetch) {
                const res = await fetch(`${API_URL}/api/ai-agents/${agent.id}/processed-emails`);
                const data = await res.json();
                if (data.success && data.processedEmails) {
                    allProcessedEmails = [...allProcessedEmails, ...data.processedEmails.map(e => ({
                        ...e,
                        agentName: agent.customName || agent.name,
                        agentId: agent.id
                    }))];
                }
            }

            // Apply date filter
            const now = new Date();
            let startDate = new Date();

            switch (dateRange) {
                case '24h':
                    startDate.setHours(startDate.getHours() - 24);
                    break;
                case '7d':
                    startDate.setDate(startDate.getDate() - 7);
                    break;
                case '30d':
                    startDate.setDate(startDate.getDate() - 30);
                    break;
                case '90d':
                    startDate.setDate(startDate.getDate() - 90);
                    break;
                case 'custom':
                    if (customStartDate) startDate = new Date(customStartDate);
                    break;
                default:
                    startDate = new Date(0); // All time
            }

            const endDate = dateRange === 'custom' && customEndDate ? new Date(customEndDate) : now;

            allProcessedEmails = allProcessedEmails.filter(email => {
                const emailDate = new Date(email.processedAt || email.createdAt);
                return emailDate >= startDate && emailDate <= endDate;
            });

            // Apply status filter
            if (statusFilter !== 'all') {
                allProcessedEmails = allProcessedEmails.filter(email => {
                    const status = email.status?.toLowerCase();
                    if (statusFilter === 'completed') {
                        return status === 'completed' || status === 'quote_sent' || status === 'booking_created' || status === 'hbl_created' || status === 'released';
                    }
                    if (statusFilter === 'pending') {
                        return status === 'pending' || status === 'draft_generated' || status === 'draft';
                    }
                    if (statusFilter === 'error') {
                        return status === 'error' || status === 'exception' || status === 'no_booking_found';
                    }
                    return status === statusFilter;
                });
            }

            // Sort by date (newest first)
            allProcessedEmails.sort((a, b) => {
                const dateA = new Date(a.processedAt || a.createdAt);
                const dateB = new Date(b.processedAt || b.createdAt);
                return dateB - dateA;
            });

            setProcessedEmails(allProcessedEmails);

            // Calculate stats
            const completed = allProcessedEmails.filter(e =>
                ['completed', 'quote_sent', 'booking_created', 'hbl_created', 'released'].includes(e.status?.toLowerCase())
            ).length;

            const quotes = allProcessedEmails.filter(e => e.quotationNumber || e.status === 'quote_sent').length;
            const bookings = allProcessedEmails.filter(e => e.bookingId || e.status === 'booking_created').length;
            const hbls = allProcessedEmails.filter(e => e.hblNumber || e.status === 'hbl_created' || e.status === 'released').length;

            setStats({
                totalProcessed: allProcessedEmails.length,
                successRate: allProcessedEmails.length > 0 ? Math.round((completed / allProcessedEmails.length) * 100) : 0,
                avgResponseTime: '2.5 min',
                totalQuotes: quotes,
                totalBookings: bookings,
                totalHBLs: hbls
            });

        } catch (error) {
            console.error('Failed to fetch filtered data:', error);
        }
    };

    // Filter by search query
    const filteredData = processedEmails.filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            item.subject?.toLowerCase().includes(query) ||
            item.sender?.toLowerCase().includes(query) ||
            item.quotationNumber?.toLowerCase().includes(query) ||
            item.hblNumber?.toLowerCase().includes(query) ||
            item.agentName?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Export to Excel
    const exportToExcel = () => {
        const exportData = filteredData.map(item => ({
            'Date': new Date(item.processedAt || item.createdAt).toLocaleString(),
            'Agent': item.agentName || item.agentId,
            'Type': item.type || 'Email Processing',
            'Subject': item.subject || '-',
            'Sender': item.sender || '-',
            'Status': item.status || '-',
            'Quote Number': item.quotationNumber || '-',
            'Booking ID': item.bookingId || '-',
            'HBL Number': item.hblNumber || '-',
            'Reference': item.referenceNo || '-'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'AI Agent Report');

        // Auto-size columns
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({
            wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length))
        }));
        ws['!cols'] = colWidths;

        XLSX.writeFile(wb, `AI_Agent_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Report exported successfully');
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (['completed', 'quote_sent', 'booking_created', 'hbl_created', 'released'].includes(statusLower)) {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Completed
            </span>;
        }
        if (['pending', 'draft_generated', 'draft'].includes(statusLower)) {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Pending
            </span>;
        }
        if (['error', 'exception', 'no_booking_found'].includes(statusLower)) {
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Error
            </span>;
        }
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
            {status || 'Processing'}
        </span>;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <div className="text-center">
                        <p className="text-xl font-semibold text-slate-700 dark:text-white">Loading Reports</p>
                        <p className="text-sm text-slate-500 dark:text-gray-400">Fetching agent data and activities...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
                            <FileText className="w-8 h-8 text-blue-500" />
                            AI Agent Reports
                        </h1>
                        <p className="text-slate-500 dark:text-gray-400 mt-1">
                            Comprehensive activity and performance reports for all AI agents
                        </p>
                    </div>
                    <button
                        onClick={exportToExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-lg"
                    >
                        <FileSpreadsheet className="w-4 h-4" />
                        Export to Excel
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-500/20">
                                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.totalProcessed}</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">Total Processed</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/20">
                                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.successRate}%</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">Success Rate</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-500/20">
                                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.avgResponseTime}</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">Avg Response</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-500/20">
                                <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.totalQuotes}</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">Quotes Generated</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-500/20">
                                <BookOpen className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.totalBookings}</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">Bookings Created</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-500/20">
                                <FileSpreadsheet className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.totalHBLs}</p>
                                <p className="text-xs text-slate-500 dark:text-gray-400">HBLs Issued</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Agent Filter */}
                        <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-slate-400" />
                            <select
                                value={selectedAgent}
                                onChange={(e) => { setSelectedAgent(e.target.value); setCurrentPage(1); }}
                                className="px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Agents</option>
                                {agents.map(agent => (
                                    <option key={agent.id} value={agent.id}>{agent.customName || agent.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range Filter */}
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <select
                                value={dateRange}
                                onChange={(e) => {
                                    setDateRange(e.target.value);
                                    setCurrentPage(1);
                                    if (e.target.value === 'custom') setShowCustomDatePicker(true);
                                }}
                                className="px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="24h">Last 24 Hours</option>
                                <option value="7d">Last 7 Days</option>
                                <option value="30d">Last 30 Days</option>
                                <option value="90d">Last 90 Days</option>
                                <option value="all">All Time</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Status</option>
                                <option value="completed">Completed</option>
                                <option value="pending">Pending</option>
                                <option value="error">Error</option>
                            </select>
                        </div>

                        {/* Search */}
                        <div className="flex-1 min-w-[200px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by subject, sender, quote #..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Refresh Button */}
                        <button
                            onClick={fetchAllData}
                            className="p-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </button>
                    </div>
                </div>

                {/* Custom Date Picker Modal */}
                <AnimatePresence>
                    {showCustomDatePicker && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                            onClick={() => setShowCustomDatePicker(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.95 }}
                                className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-xl max-w-md w-full mx-4"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Select Date Range</h3>
                                    <button onClick={() => setShowCustomDatePicker(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                                        <X className="w-5 h-5 text-slate-500" />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            value={customStartDate}
                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">End Date</label>
                                        <input
                                            type="date"
                                            value={customEndDate}
                                            onChange={(e) => setCustomEndDate(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                                        />
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowCustomDatePicker(false);
                                            fetchFilteredData();
                                        }}
                                        className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Data Table */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Date/Time</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Agent</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Subject</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Sender</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Output</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {paginatedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-12 text-center">
                                            <div className="flex flex-col items-center text-slate-400">
                                                <FileText className="w-12 h-12 mb-3 opacity-50" />
                                                <p className="text-sm">No data found for the selected filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedData.map((item, idx) => (
                                    <tr key={item.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-slate-800 dark:text-white">
                                                {new Date(item.processedAt || item.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {new Date(item.processedAt || item.createdAt).toLocaleTimeString()}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400">
                                                {item.agentName || item.agentId}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-slate-800 dark:text-white max-w-xs truncate">
                                                {item.subject || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-slate-600 dark:text-slate-300">
                                                {item.sender || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(item.status)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-slate-600 dark:text-slate-300">
                                                {item.quotationNumber && <span className="block">Quote: {item.quotationNumber}</span>}
                                                {item.bookingId && <span className="block">Booking: {item.bookingId}</span>}
                                                {item.hblNumber && <span className="block">HBL: {item.hblNumber}</span>}
                                                {!item.quotationNumber && !item.bookingId && !item.hblNumber && '-'}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} entries
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                </button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AIAgentReports;
